import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent } from '@theme/events';

/** Build only unambiguous media-to-color assignments. Detail images have no entry. */
export function buildMediaColorMap(assignments) {
  const colors = new Map();
  for (const [mediaId, color] of assignments) {
    if (mediaId == null || !color) continue;
    const id = String(mediaId);
    if (!colors.has(id)) colors.set(id, color);
    else if (colors.get(id) !== color) colors.set(id, null);
  }
  return colors;
}

/**
 * Product gallery with two-way color selection.
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #colorMap = new Map();
  #productId;
  #colorOptionName;
  #selectingFromGallery = false;
  #preserveGallery = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    const mapping = this.querySelector('script[data-gallery-color-map]');
    if (mapping) {
      const data = JSON.parse(mapping.textContent);
      this.#productId = String(data.productId);
      this.#colorOptionName = data.optionName;
      this.#colorMap = buildMediaColorMap(data.media);
    }

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleVariantSelected = () => {
    // A manual swatch/size choice resumes the normal picker-to-photo behavior.
    if (!this.#selectingFromGallery) this.#preserveGallery = false;
  };

  #handleVariantUpdate = (event) => {
    if (this.#productId && String(event.detail.data.productId) !== this.#productId) return;
    // An asynchronous color response must not pull the customer off a newer photo.
    if (this.#preserveGallery) return;
    const featuredMediaId = event.detail.resource?.featured_media?.id;
    if (featuredMediaId) this.slideshow?.select({ id: featuredMediaId }, undefined, { animate: false });
  };

  #handleSlideshowSelect = (event) => {
    if (event.target !== this.slideshow || !event.detail.userInitiated) return;
    this.#selectColorForMedia(event.detail.id);
  };

  #selectColorForMedia(mediaId) {
    this.#preserveGallery = true;
    const color = this.#colorMap.get(String(mediaId));
    if (!color) return;
    const target = this.closest('.shopify-section, dialog');
    const picker = [...(target?.querySelectorAll('variant-picker') ?? [])].find(
      (element) => element.dataset.productId === this.#productId
    );
    const option = [...(picker?.querySelectorAll('[data-option-name]') ?? [])].find(
      (element) => element.dataset.optionName === this.#colorOptionName
    );
    const input = [...(option?.querySelectorAll('input[type="radio"]') ?? [])].find(
      (element) => element.value === color
    );
    if (!input || input.checked || input.disabled) return;

    // Use the existing picker pipeline: keep size, resolve price/availability,
    // disable Add to Cart while loading, then update the form's actual variant ID.
    this.#selectingFromGallery = true;
    try {
      input.click();
    } finally {
      this.#selectingFromGallery = false;
    }
  }

  #handleZoomMediaSelected = (event) => {
    const slide = this.slideshow?.slides[event.detail.index];
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
    if (slide) this.#selectColorForMedia(slide.getAttribute('slide-id'));
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
